import { useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { FaArrowLeft, FaPencil, FaTrash } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { useDataViews } from "../api/useDataViews";
import { useUpdateDataView } from "../api/useUpdateDataView";
import { useDeleteDataView } from "../api/useDeleteDataView";
import { DataViewForm, type DataViewFormValues } from "./DataViewForm";
import { TABLE_VIEW_TYPE } from "../viewTypes/table";
import { useAppSelector } from "../../../redux/hooks";
import { useBuildScopeDefinition } from "../hooks/useBuildScopeDefinition";
import type { DataView, DataViewWriteBody } from "../../../zodios/apiDefinitions/api/dataViews";
import Loading from "../../../components/core/blocks/Loading";

type ManageDataViewsModalProps = {
  datasetKey: string;
};

export const ManageDataViewsModal = NiceModal.create(({ datasetKey }: ManageDataViewsModalProps) => {
  const modal = useModal();
  const { t } = useTranslation(["dataViews"]);
  const currentUserId = useAppSelector((state) => state.user.user_id);
  const buildScopeDefinition = useBuildScopeDefinition();
  const listQuery = useDataViews({ viewType: TABLE_VIEW_TYPE, datasetKey });
  const updateMutation = useUpdateDataView();
  const deleteMutation = useDeleteDataView();
  const [editing, setEditing] = useState<DataView | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null);

  const views = listQuery.data?.results ?? [];

  const scopeLabel = (view: DataView) =>
    view.scope_definition.scope === "MARKETPLACE" ? t(($) => $.dataViews.Marketplace)
    : view.scope_definition.scope === "ORGANIZATION" ? t(($) => $.dataViews.Organization)
    : t(($) => $.dataViews["Any organization"]);

  const visibilityLabel = (view: DataView) =>
    view.scope_definition.visibility === "USER" ? t(($) => $.dataViews["Only me"])
    : view.scope_definition.visibility === "STAFF" ? t(($) => $.dataViews.Staff)
    : t(($) => $.dataViews.Anyone);

  const handleEditSubmit = (values: DataViewFormValues) => {
    if (!editing) return;
    const body: DataViewWriteBody = {
      name: values.name,
      description: values.description,
      view_type: editing.view_type,
      dataset_key: editing.dataset_key,
      fields: editing.fields,
      filters: editing.filters,
      grouping: editing.grouping,
      sorting: editing.sorting,
      search: editing.search,
      marketplace: editing.marketplace,
      scope_definition: buildScopeDefinition(values.scope, values.visibility),
    };
    updateMutation.mutate({ id: editing.id, body }, { onSuccess: () => setEditing(null) });
  };

  const handleConfirmDelete = (view: DataView) => {
    deleteMutation.mutate(view.id, { onSuccess: () => setConfirmingDeleteId(null) });
  };

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => !isOpen && modal.remove()}
      title={
        editing ?
          t(($) => $.dataViews["Edit view: {name}"], {
            name: editing.name.length > 40 ? `${editing.name.slice(0, 40)}…` : editing.name,
          })
        : t(($) => $.dataViews["Saved views"])
      }
      css={{ minWidth: "640px", maxWidth: "800px" }}
    >
      {editing ?
        <>
          <HStack paddingX="xlarge" paddingTop="large">
            <Button variant="contentOnly" size="small" onPress={() => setEditing(null)}>
              <HStack gap="xsmall" alignItems="center">
                <FaArrowLeft />
                <span>{t(($) => $.dataViews["Back to views"])}</span>
              </HStack>
            </Button>
          </HStack>
          <DataViewForm
            initial={{
              name: editing.name,
              description: editing.description,
              scope: editing.scope_definition.scope,
              visibility: editing.scope_definition.visibility,
            }}
            submitLabel={t(($) => $.dataViews["Save changes"])}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditing(null)}
            isSaving={updateMutation.isPending}
            existingNames={views.filter((v) => v.id !== editing.id).map((v) => v.name)}
          />
        </>
      : <VStack gap="medium" alignItems="stretch" paddingX="xlarge" paddingY="large">
          {listQuery.isLoading ?
            <Loading fullPage={false} height={"50px"} />
          : views.length === 0 ?
            <p className={css({ color: "text.secondary", textAlign: "center" })}>
              {t(($) => $.dataViews["No saved views yet. Save the current view to get started."])}
            </p>
          : <VStack gap="xsmall" alignItems="stretch">
              {views.map((view) => {
                const canManage = String(view.created_by ?? "") === currentUserId;
                return (
                  <HStack
                    key={view.id}
                    justifyContent="space-between"
                    gap="medium"
                    paddingY="small"
                    paddingX="medium"
                    className={css({
                      borderBottom: "1px solid {colors.border.default}",
                      _last: { borderBottom: "none" },
                    })}
                  >
                    <VStack alignItems="start" gap="xxsmall" flex="1" minWidth="0">
                      <span className={css({ fontWeight: "600" })}>{view.name}</span>
                      {view.description && (
                        <span
                          className={css({
                            color: "text.secondary",
                            fontSize: "small",
                            overflow: "hidden",
                            lineClamp: 2,
                          })}
                          title={view.description}
                        >
                          {view.description}
                        </span>
                      )}
                      <span className={css({ color: "text.secondary", fontSize: "xsmall" })}>
                        {scopeLabel(view)} · {visibilityLabel(view)}
                      </span>
                    </VStack>
                    {confirmingDeleteId === view.id ?
                      <VStack gap="small">
                        <span className={css({ fontSize: "small", color: "text.secondary" })}>
                          {t(($) => $.dataViews["Delete this view?"])}
                        </span>
                        <HStack gap={"large"}>
                          <Button
                            variant="secondary"
                            onPress={() => setConfirmingDeleteId(null)}
                            isDisabled={deleteMutation.isPending}
                          >
                            {t(($) => $.dataViews.Cancel)}
                          </Button>
                          <Button
                            variant="primary"
                            colorScheme="destructive"
                            onPress={() => handleConfirmDelete(view)}
                            isDisabled={deleteMutation.isPending}
                          >
                            {t(($) => $.dataViews.Delete)}
                          </Button>
                        </HStack>
                      </VStack>
                    : canManage ?
                      <HStack gap="medium">
                        <Button
                          variant="contentOnly"
                          onPress={() => setEditing(view)}
                          isDisabled={updateMutation.isPending}
                          tooltip={t(($) => $.dataViews.Edit)}
                        >
                          <FaPencil />
                        </Button>
                        <Button
                          variant="contentOnly"
                          onPress={() => setConfirmingDeleteId(view.id)}
                          isDisabled={deleteMutation.isPending}
                          tooltip={t(($) => $.dataViews.Delete)}
                        >
                          <FaTrash />
                        </Button>
                      </HStack>
                    : <span className={css({ fontSize: "small", color: "text.secondary", fontStyle: "italic" })}>
                        {t(($) => $.dataViews["Read-only"])}
                      </span>
                    }
                  </HStack>
                );
              })}
            </VStack>
          }
          <HStack justifyContent="center" paddingTop="medium">
            <Button variant="secondary" onPress={() => modal.remove()}>
              {t(($) => $.dataViews.Close)}
            </Button>
          </HStack>
        </VStack>
      }
    </Modal>
  );
});
